import { mapRecordValues, notEmpty, notUndefined } from "@de-xima/xima-common-js-lang";
import type { AllowFunc, CalendarOptions, CustomButtonInput, EventInput } from "@fullcalendar/core";
import jQuery from "jquery";

import type { IScheduleExtenderOptions, IScheduleProxyButton } from "./Header.ts";
import { findTooltipWidgetFor } from "./Util.ts";

type TEventMountArg = Parameters<Exclude<CalendarOptions["eventDidMount"], undefined>>[0];

const DefaultExtenderOptions: IScheduleExtenderOptions = {
  businessHours: [],
  constrainToSameDay: false,
  deleteCallback: undefined,
  deleteTitle: "",
  eventLimit: {},
  firstDay: undefined,
  proxyButtons: {},
  responsive: false,
};

const MillisecondsInDay = 24 * 60 * 60 * 1000;

let titleId = 0;

function generateTitleClass(): string {
  titleId += 1;
  return `fc-title-gen-${titleId}`;
}

/**
 * @param element Element to check.
 * @returns string Tooltip of the element
 */
function findTooltipFor(element: JQuery): string {
  const titleAttr = element.attr("title");
  if (notEmpty(titleAttr)) {
    return titleAttr;
  }
  const tooltipWidget = findTooltipWidgetFor(element);
  if (tooltipWidget !== undefined) {
    return tooltipWidget.jq.text();
  }
  return "";
}

/**
 * @param widget Schedule widget.
 * @param proxyButton Button data.
 * @param titleMap Map of title attributes for the button.
 * @param name Name of the button.
 * @returns The newly created full calendar button.
 */
function createProxyButton(
  _widget: PrimeFaces.widget.Schedule,
  titleMap: Map<string, string>,
  proxyButton: IScheduleProxyButton,
): CustomButtonInput {
  const proxy = jQuery(proxyButton.proxy);
  const className = generateTitleClass();
  titleMap.set(className, findTooltipFor(proxy) ?? "");
  return {
    click: () => {
      jQuery(proxyButton.proxy).trigger("click");
    },
    icon: `${proxyButton.icon ?? ""} fc-custom-button ${className}`,
    text: proxyButton.text ?? proxy.text() ?? "",
  };
}

/**
 * Applies all requested custom buttons to the full calendar.
 * @param widget Schedule widget.
 * @param options Extender options.
 */
function applyCustomButtons(widget: PrimeFaces.widget.Schedule, options: IScheduleExtenderOptions): void {
  const titleMap = new Map<string, string>();
  widget.cfg.options ??= {};
  widget.cfg.options.customButtons = {
    ...mapRecordValues(options.proxyButtons, createProxyButton.bind(window, widget, titleMap)),
  };
  // Workaround to add title attribute, not supported otherwise
  widget.cfg.options.viewDidMount = ({ el }) => {
    for (const button of jQuery(el).closest(".fc").find(".fc-custom-button").toArray()) {
      const title = (jQuery(button).attr("class") ?? "")
        .split(/\s+/)
        .map((c) => titleMap.get(c))
        .filter(notUndefined)[0];
      jQuery(button)
        .closest("button")
        .attr("title", title ?? "");
    }
  };
}

/**
 * @param data Data when an event is mounted to the DOM.
 * @param options Extender options for remove button.
 */
function applyRemoveButton(data: TEventMountArg, options: IScheduleExtenderOptions): void {
  const deleteCallback = options.deleteCallback;
  if (deleteCallback !== undefined) {
    const $el = jQuery(data.el);
    const $removeBtn = jQuery('<span class="fc-remove fa fa-times"></span>');
    $removeBtn.attr("title", options.deleteTitle ?? "");
    if (notEmpty(data.event.textColor)) {
      $removeBtn.css("color", data.event.textColor);
    }
    $el.append($removeBtn);
    $removeBtn.on("click", (event) => {
      event.stopPropagation();
      // Update UI immediately
      data.event.remove();
      // Sync with server
      deleteCallback(data.event.id);
    });
  }
}

/**
 * @param widget Current schedule widget.
 * @param options Configured options.
 */
function applyResponsive(widget: PrimeFaces.widget.Schedule, options: IScheduleExtenderOptions): void {
  if (options.responsive) {
    widget.cfg.options ??= {};
    widget.cfg.options.height = "parent";
    widget.cfg.options.handleWindowResize = true;
    delete widget.cfg.options.aspectRatio;
  }
  if (typeof ResizeObserver === "function") {
    const callback = (_entries: ResizeObserverEntry[], _observer: ResizeObserver) => {
      try {
        // showing a popover may trigger a resize
        // updating the size closes the popover
        // so dont update when popover is visible
        if (!jQuery(".fc-popover").is(":visible")) {
          widget.calendar?.updateSize();
        }
      } catch {
        console.error("Could not resize calendar");
      }
    };
    const observer = new ResizeObserver(callback);
    const target = widget.jq.parent().get(0);
    if (target !== undefined) {
      observer.observe(target);
      widget.addDestroyListener(() => {
        observer.unobserve(target);
      });
      widget.addRefreshListener(() => {
        observer.observe(target);
        widget.addDestroyListener(() => {
          observer.unobserve(target);
        });
      });
    }
  }
}

/**
 * @param widget Current schedule widget.
 * @param options Configured options.
 */
function applyEventLimit(widget: PrimeFaces.widget.Schedule, options: IScheduleExtenderOptions): void {
  if (typeof options.eventLimit === "object") {
    widget.cfg.options ??= {};
    widget.cfg.options.views = {
      dayGridDay:
        options.eventLimit.day !== undefined
          ? {
              dayMaxEvents: options.eventLimit.day,
            }
          : {},
      dayGridMonth:
        options.eventLimit.month !== undefined
          ? {
              dayMaxEvents: options.eventLimit.month,
            }
          : {},
      dayGridWeek:
        options.eventLimit.week !== undefined
          ? {
              dayMaxEvents: options.eventLimit.week,
            }
          : {},
    };
    // TODO: Bug in PF10 https://github.com/primefaces/primefaces/issues/7746 Remove this line once that is fixed
    (widget.cfg as unknown as Record<string, unknown>).views = widget.cfg.options.views;
  }
}

/**
 * @param widget Current schedule widget.
 * @param options Configured options.
 */
function applyFirstDay(widget: PrimeFaces.widget.Schedule, options: IScheduleExtenderOptions): void {
  widget.cfg.options ??= {};
  if (options.firstDay !== undefined) {
    widget.cfg.options.firstDay = options.firstDay;
  } else {
    delete widget.cfg.options.firstDay;
  }
}

/**
 * @param options Options for the schedule extender.
 * @returns Constraint for the same day so events cannot move to another day.
 */
function createSameDayConstraint(): AllowFunc {
  return (dropInfo) => {
    const sameDay =
      dropInfo.start.getUTCFullYear() === dropInfo.end.getUTCFullYear() &&
      dropInfo.start.getUTCMonth() === dropInfo.end.getUTCMonth() &&
      dropInfo.start.getUTCDay() === dropInfo.end.getUTCDay();
    const duration = dropInfo.end.getTime() - dropInfo.start.getTime();
    const endOnMidnight =
      dropInfo.end.getUTCHours() === 0 &&
      dropInfo.end.getUTCMinutes() === 0 &&
      dropInfo.end.getUTCSeconds() === 0 &&
      dropInfo.end.getUTCMilliseconds() === 0;
    return sameDay || (duration < MillisecondsInDay && endOnMidnight);
  };
}

/**
 * @param widget Current schedule widget.
 * @param options Configured options.
 */
function applyEventAllow(widget: PrimeFaces.widget.Schedule, options: IScheduleExtenderOptions): void {
  const constraints = [...(options.constrainToSameDay ? [createSameDayConstraint()] : [])];
  widget.cfg.options ??= {};
  widget.cfg.options.eventAllow = (dropInfo, draggedEvent) => {
    return constraints.every((constraint) => constraint(dropInfo, draggedEvent));
  };
}

/**
 * @param widget Current schedule widget.
 * @param options Configured options.
 */
function applyBusinessHours(widget: PrimeFaces.widget.Schedule, options: IScheduleExtenderOptions): void {
  if (Array.isArray(options.businessHours) && options.businessHours.length > 0) {
    widget.cfg.options ??= {};
    widget.cfg.options.businessHours = options.businessHours;
  }
}

/**
 * Fix for https://github.com/fullcalendar/fullcalendar/issues/6966
 *
 * Remove as soon as the issue is fixed and the new full calendar version is
 * included in PrimeFaces.
 * @param data Data when the event was mounted to the DOM.
 */
function applyColorFix(data: TEventMountArg): void {
  if (notEmpty(data.backgroundColor)) {
    data.el.style.backgroundColor = data.backgroundColor;
  }
  if (notEmpty(data.textColor)) {
    data.el.style.color = data.textColor;
  }
  if (notEmpty(data.borderColor)) {
    data.el.style.borderColor = data.borderColor;
  }
}

/**
 * Creates the extender for the Schedule widget, adding additional
 * functionality.
 * @param options Options for the extender.
 * @returns The extender that can be used for `<p:schedule extender="..."/>`
 */
export function createExtender(
  options: Partial<IScheduleExtenderOptions> = {},
): PrimeFaces.widget.Schedule.ScheduleExtender {
  const opts = jQuery.extend({}, DefaultExtenderOptions, options);
  return function (this: PrimeFaces.widget.Schedule) {
    applyResponsive(this, opts);
    applyCustomButtons(this, opts);
    applyEventAllow(this, opts);
    applyBusinessHours(this, opts);
    applyEventLimit(this, opts);
    applyFirstDay(this, opts);

    this.cfg.options ??= {};
    this.cfg.options.eventDidMount = (data) => {
      applyRemoveButton(data, opts);
      applyColorFix(data);
    };
  };
}

/**
 * Creates the schedule extender for the inbox appointment view.
 * @param businessHours The available slots of all appointment templates.
 * @returns Newly created extender.
 */
export function createInboxScheduleExtender(businessHours: EventInput[]): PrimeFaces.widget.Schedule.ScheduleExtender {
  return createExtender({
    businessHours: businessHours,
    eventLimit: { month: 6 },
    proxyButtons: {
      refresh: {
        icon: " fa fa-sync",
        proxy: ".inbox-toggle-refresh-button",
      },
      toggleView: {
        icon: " fa fa-bars",
        proxy: ".inbox-toggle-view-button",
      },
    },
    responsive: true,
  });
}

/**
 * Creates the schedule extender for the backend configuration of the appointment template.
 * @param onDeleteAppointmentSlot Callback for when the delete button is pressed.
 * @param deleteTitle Title for the delete button.
 * @returns Newly created extender.
 */
export function createBackendAppointmentExtender(
  onDeleteAppointmentSlot: () => void,
  deleteTitle: string,
): PrimeFaces.widget.Schedule.ScheduleExtender {
  return createExtender({
    constrainToSameDay: true,
    deleteCallback: onDeleteAppointmentSlot,
    deleteTitle: deleteTitle,
    firstDay: 1,
  });
}
